import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { isMobileOnly } from 'react-device-detect';

// ActionCreators

import { changePage, switchHyperReview } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { isFormInInitialState, useExchangeAndProgramNames } from '@reducers/appReducer';
import { preventerTools } from '@utils/tools/preventerTools';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { chunk } from '@utils/miscellaneous/objectHelpers';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Left Navigation Bar
 * @constructor
 */
const SsapNavBar = props => {

    const {
        adjustedFlow = [],
        data: {
            maxAchievedPage
        },
        exception: {
            exceptionType
        },
        householdMembers = [],
        hyperReview,
        isSsapFormsInInitialsState,
        methods,
        page
    } = props;

    const currentSection = adjustedFlow[page] && adjustedFlow[page].sectionIndex;

    const currentPage = adjustedFlow[page];

    const formName = adjustedFlow[page].formName;

    const { exchangeMedicaidSlashChip } = useExchangeAndProgramNames();

    // Here we prepare data for NavBar creation
    // We transform list of pages what includes tech data and Components themselves
    // to list of sections (with subsections) to iterate from it and build NavBar

    // 1. Iteration through list of pages and creation and combining subsections into arrays

    const adjustedSections = [];

    adjustedFlow.map(page => {

        const index = adjustedSections.findIndex( section => section.sectionIndex === page.sectionIndex);

        if(~index) {

            // Here we add to existing subsections in current section new subsection

            adjustedSections[index] = {
                ...adjustedSections[index],
                subSections: [
                    ...adjustedSections[index].subSections,
                    page
                ]
            };
        } else {

            // Here we create section if it does not exist, assign section name, index and
            // create subsections and add to it first current subsection

            adjustedSections[adjustedSections.length] = {
                sectionName: page.sectionName,
                sectionIndex: page.sectionIndex,
                subSections: [
                    page
                ]
            };
        }

    });

    // 2. Refining subsections into sub-arrays if they are common for all household members

    const dataForRender = adjustedSections.map(section => {

        let subsections = [];

        let loopArr = [];

        // 2.1. Grouping subsections in sub-array if we see a loop for household members

        section.subSections.forEach((subsection, idx) => {
            if(subsection.hhm === -1 ) {
                subsections = [
                    ...subsections,
                    loopArr
                ];
                loopArr = [];
                subsections[idx] = subsection;
            }
            else {
                loopArr = [
                    ...loopArr,
                    subsection
                ];
            }
        });

        // 2.2. Dividing into chunks to support subheader like person name

        const chunkedSubsections = subsections.map(subsection => {

            if(Array.isArray(subsection)){
                return chunk(subsection, subsection.length/householdMembers.length);
            } else {
                return subsection;
            }
        });

        return {
            ...section,
            subSections: chunkedSubsections.filter(subsection => subsection) // Getting rid of empty elements in array
        };
    });

    const getClassNameForSubSection = subSection => {

        const {
            pageIndexInFlow,
        } = subSection;

        switch(true) {
            case pageIndexInFlow === page:
                return 'gi-sublist__items--current';
            case pageIndexInFlow <= maxAchievedPage:
                return 'gi-sublist__items--done';
            default:
                return 'gi-sublist__items--undone';
        }
    };

    const getClassNameForSubSubSection = subSection => {
        const {
            pageIndexInFlow
        } = subSection;

        switch(true) {
            case pageIndexInFlow === page:
                return 'usa-sidenav__item gi-sublist__level__3__items member-loop--current';
            case pageIndexInFlow <= maxAchievedPage:
                return  'usa-sidenav__item gi-sublist__level__3__items member-loop--done';
            default:
                return 'usa-sidenav__item gi-sublist__level__3__items member-loop--undone';
        }
    };

    const getClassNameForHhm = subsections => {
        const isAnyActive = subsections.some(ss => ss.isActive);

        switch(true) {
            case subsections.some(ss => ss.pageIndexInFlow === page):
                return 'gi-sublist__level__2__items-links--current';
            case subsections.some(ss => ss.pageIndexInFlow < maxAchievedPage) && isAnyActive:
                return 'gi-sublist__level__2__items-links--done';
            default:
                return 'gi-sublist__level__2__items-links--undone';
        }
    };

    const getClassNameForSection = subsections => {

        const flattenSections = subsections.reduce((res, ss) => {
            return Array.isArray(ss) ?
                [
                    ...res,
                    ...ss.reduce((res, ss) => {
                        return [
                            ...res,
                            ...ss
                        ];
                    }, [ ])
                ] :
                [
                    ...res,
                    ss
                ];
        }, [ ]);

        switch(true) {
            case flattenSections.some(ss => ss.pageIndexInFlow === page):
                return 'gi-leftnav__items-links--current';
            case flattenSections.some(ss =>  ss.pageIndexInFlow <= maxAchievedPage):
                return 'gi-leftnav__items-links--done';
            default:
                return 'gi-leftnav__items-links--undone';

        }
    };

    const [ isVisible, setVisible ] = useState(true);

    const handleMobileClick = () => {
        setVisible(!isVisible); 
    };


    const getLinks = () => {

        const {
            householdMembers,
        } = props;

        const handleFormCheck = action => {
            if(!isSsapFormsInInitialsState) {
                props.actions.showModal({
                    modalType: MODAL_TYPES.LEAVE_PAGE_MODAL,
                    modalData: {
                        action,
                    }
                });
            } else {
                return action();
            }
        };

        const switchPage = destination => {
            formName && methods[formName] ?
                methods[formName].onFormExit([() => props.actions.changePage(destination)]) :
                props.actions.changePage(destination);
            if(destination !== maxAchievedPage) {
                !hyperReview && props.actions.switchHyperReview(true);
            } else {
                hyperReview && props.actions.switchHyperReview(false);
            }
        };

        const handleSectionClick = (e, idx) => {

            e.preventDefault();

            if(exceptionType) {
                return null;
            }

            const firstAvailablePageNumber =
                adjustedFlow
                    .reduce(
                        (firstPageIndex, p, pageIndex) => {
                            return p.sectionIndex === idx && p.isActive && !firstPageIndex
                                ? pageIndex
                                : firstPageIndex;
                        },
                        null
                    );

            if(idx <= adjustedFlow[maxAchievedPage].sectionIndex) {
                if (firstAvailablePageNumber !== null && firstAvailablePageNumber !== page) {
                    handleFormCheck(() => switchPage(firstAvailablePageNumber));
                }
                return null;
            }
        };

        const handleLinkClick = (e, idx) => {

            e.preventDefault();

            if(exceptionType) {
                return null;
            }

            if (idx <= maxAchievedPage && idx !== page) {
                handleFormCheck(() => switchPage(idx));
            }

        };

        const handleHouseholdMemberClick = (e, ss, sectionIdx) => {

            e.preventDefault();

            const [{ hhm }] = preventerTools.provideArray(ss, [{ }]);

            if(!ss.some(sss => sss.isActive)) {
                return;
            }

            const firstPageInLoop = adjustedFlow.find(page => page.hhm === hhm && page.sectionIndex === sectionIdx && page.isActive);

            if(firstPageInLoop){
                if (firstPageInLoop.pageIndexInFlow < maxAchievedPage && firstPageInLoop.pageIndexInFlow !== page) {
                    handleFormCheck(() => switchPage(firstPageInLoop.pageIndexInFlow));
                }
            }

            if(exceptionType) {
                return null;
            }
        };

        return dataForRender.map(section => {

            const {
                sectionName,
                sectionIndex,
                subSections = []
            } = section;

            return (
                <li
                    key={ sectionName }
                    className="usa-sidenav__item gi-leftnav__items"
                >
                    <a
                        href="#"
                        className={getClassNameForSection(subSections)}
                        onClick={e => handleSectionClick(e, sectionIndex)}
                    >
                        <FormattedMessage id={sectionName}/>
                    </a>
                    <ul
                        className={`usa-sidenav__sublist gi-sublist__level__1  ${currentSection === sectionIndex ? 'shown' : 'hidden'}`}
                    >
                        {
                            subSections.map((subSection, subSectionIndex) => {

                                if(subSection.isActive === false) {
                                    return null;
                                }

                                const isLoop = Array.isArray(subSection);

                                const checkIfPersonExtended = pages => {
                                    const {
                                        section,
                                        pageName,
                                        hhm,
                                    } = currentPage;

                                    return pages.some(page => page.pageName === pageName && page.section === section && page.hhm === hhm);
                                };

                                const subSectionClass = !isLoop
                                    ? getClassNameForSubSection(subSection)
                                    : 'gi-sublist__items--house-member';

                                return (
                                    <li
                                        key={`${subSection.pageName}_${subSectionIndex}`}
                                        onClick={e => handleLinkClick(e, subSection.pageIndexInFlow)}
                                        className={`usa-sidenav__item gi-sublist__level__1__items ${subSectionClass}`}
                                    >
                                        {
                                            !isLoop ?
                                                <a href="#" className="gi-sublist__level__1__items-links" aria-current={subSectionClass.includes('gi-sublist__items--current') ? 'page' : undefined}>
                                                    <FormattedMessage id={subSection.page.navBarText} values={{ exchangeMedicaidSlashChip }} />
                                                </a>:

                                                <Fragment>
                                                    <FormattedMessage
                                                        id="ssap.nav_bar.household_members"
                                                        values={{ householdMembers: householdMembers.length }}
                                                    />
                                                    <ul  className='usa-sidenav__sublist gi-sublist__level__2'>
                                                        {
                                                            subSection.map((ss, idx) => {

                                                                return (
                                                                    <li className="usa-sidenav__item gi-sublist__level__2__items" key={`${ss[0].pageName}_${idx}`}>
                                                                        {
                                                                            ss[0].hhm <= householdMembers.length - 1 ?

                                                                                <Fragment>
                                                                                    <a className={`text-break ${getClassNameForHhm(ss)}`} href="#" onClick={e => handleHouseholdMemberClick(e, ss, sectionIndex)}>
                                                                                        {normalizeNameOfPerson(householdMembers[ss[0].hhm].name)}</a>

                                                                                    {
                                                                                        checkIfPersonExtended(ss) &&

                                                                                        <ul className='usa-sidenav__sublist gi-sublist__level__3'>
                                                                                            {
                                                                                                ss.map((sss, sssIndex) => {
                                                                                                    const subSubSectionClassName = getClassNameForSubSubSection(sss);
                                                                                                    return (
                                                                                                        sss.isActive ?
                                                                                                            <li
                                                                                                                key={`${sss.page.navBarText}_${idx}`}
                                                                                                                id={`sublink-header_hhm${ss[0].hhm}_link${sssIndex}`}
                                                                                                                className={subSubSectionClassName}
                                                                                                                onClick={e => handleLinkClick(e, sss.pageIndexInFlow)}
                                                                                                            >
                                                                                                                <a className="gi-sublist__level__3__items-links" href="#" aria-current={subSubSectionClassName.includes('member-loop--current') ? 'page' : undefined}>
                                                                                                                    <FormattedMessage
                                                                                                                        id={sss.page.navBarText}/>
                                                                                                                </a>
                                                                                                            </li> : null
                                                                                                    );
                                                                                                })
                                                                                            }
                                                                                        </ul>
                                                                                    }

                                                                                </Fragment> :
                                                                                null
                                                                        }
                                                                    </li>

                                                                );
                                                            })
                                                        }
                                                    </ul>
                                                </Fragment>
                                        }
                                    </li>
                                );
                            })
                        }
                    </ul>
                </li>
            );
        });
    };

    return (
        isMobileOnly ?
            <Fragment>
                <ul className="mobile-nav__list">
                    <li className="mobile-nav__list__item" onClick={handleMobileClick} >
                        <h3 className={`mobile-nav__list__item__header ${ isVisible ? 'mobile--chevron-up' : 'mobile--chevron-down'}`}>
                            <FormattedMessage id='ssap.nav_bar.steps'/>
                        </h3>
                    </li>
                    <li>
                        <nav aria-label="Secondary navigation" className={`mobile-nav__secondary-nav-container ${ isVisible ? 'mobile--shown' : 'mobile--hidden'}`}>
                            <ul className="usa-sidenav gi-leftnav mobile-nav__secondary-nav-container__list--slim">
                                
                                {
                                    getLinks()
                                }
                            </ul>
                        </nav>
                    </li>
                </ul>   
            </Fragment>
            :
            <Fragment>
                <div className="header">
                    <h3 className="nav-heading">
                        <FormattedMessage id='ssap.nav_bar.steps'/>
                    </h3>
                </div>
                <nav aria-label="Secondary navigation">
                    <ul className="usa-sidenav gi-leftnav">
                        {
                            getLinks()
                        }
                    </ul>
                </nav>
            </Fragment>
    );
};

const mapStateToProps = state => ({

    adjustedFlow: state.ssap.adjustedFlow,
    data: state.ssap.data,
    exception: state.ssap.exception,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    hyperReview: state.ssap.hyperReview,
    isSsapFormsInInitialsState: isFormInInitialState(state),
    methods: state.ssap.methods,
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changePage,
        showModal,
        switchHyperReview
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(SsapNavBar);
